'use client';
import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { useContentAnalytics } from '@dotcms/analytics/react';
import { getDotcmsImageURL } from '@/app/utils/getDotcmsImageURL';
import { AnalyticsConfig } from '@/app/utils/analytics';

const FALLBACK_IMAGE = '/assets/backgrounds/dotcms-demo-bg.svg';
const PAINT_OFFSET = 300;

function IFrameCode({
    frameCode,
    previewImage,
    thumbnail,
    useFrame = false,
    buttonLabel = 'Explore Live Demo',
    trackConversion = false,
    conversionKey,
    aspectRatio,
    maxHeight,
    eyebrow,
    onReveal
}) {
    const contentRef = useRef(null);
    const [iframeReady, setIframeReady] = useState(false);
    const [revealed, setRevealed] = useState(false);
    const { conversion } = useContentAnalytics(AnalyticsConfig);

    useEffect(() => {
        const container = contentRef.current;
        if (!container) return;

        if (!frameCode) {
            container.innerHTML = '';
            return;
        }

        // Reset overlay state whenever frameCode changes so the new iframe
        // goes through the same loading/reveal flow as the initial one.
        setIframeReady(false);
        setRevealed(false);

        // Set innerHTML imperatively — never via dangerouslySetInnerHTML.
        // This prevents React from resetting innerHTML on re-renders, which would
        // reload the iframe every time state changes.
        container.innerHTML = frameCode;

        let listenerCleanup = () => {};
        const fallback = setTimeout(() => setIframeReady(true), 5000);

        const attachToIframe = (iframe) => {
            // Already loaded (cached)
            if (iframe.contentDocument?.readyState === 'complete') {
                setTimeout(() => setIframeReady(true), PAINT_OFFSET);
                return;
            }
            const onLoaded = () => setTimeout(() => setIframeReady(true), PAINT_OFFSET);
            iframe.addEventListener('load', onLoaded);
            listenerCleanup = () => iframe.removeEventListener('load', onLoaded);
        };

        const iframe = container.querySelector('iframe');

        if (iframe) {
            attachToIframe(iframe);
        } else {
            // frameCode may inject the iframe via <script> — watch for it
            const observer = new MutationObserver(() => {
                const injected = container.querySelector('iframe');
                if (injected) {
                    observer.disconnect();
                    attachToIframe(injected);
                }
            });
            observer.observe(container, { childList: true, subtree: true });
            listenerCleanup = () => observer.disconnect();
        }

        return () => {
            listenerCleanup();
            clearTimeout(fallback);
        };
    }, [frameCode]);

    if (!frameCode) return null;

    const handleReveal = () => {
        if (!iframeReady) return;

        if (trackConversion) {
            const eventName =
                conversionKey?.trim() || buttonLabel.trim().toLowerCase().replace(/\s+/g, '-');
            conversion(eventName);
        }

        setRevealed(true);
        onReveal?.();
    };

    const imageSrc =
        thumbnail && thumbnail.identifier
            ? getDotcmsImageURL({ src: thumbnail.identifier })
            : previewImage || FALLBACK_IMAGE;

    return (
        <div className="w-full">
            {eyebrow && (
                <p className="mb-6 text-center text-[13px] font-semibold uppercase tracking-widest text-gray-400">
                    {eyebrow}
                </p>
            )}
            <div
                className="w-full overflow-hidden rounded-2xl border border-gray-200 bg-gray-400 shadow-[0_25px_50px_-12px_hsl(293_69%_49%_/_0.15)]"
                style={{ animation: 'frameSlideUp 0.6s cubic-bezier(0.16,1,0.3,1) 0.5s both' }}>
            {/* Browser chrome */}
            {useFrame && (
                <div className="flex items-center border-b border-gray-200 bg-gray-100 px-4 py-3">
                    <div className="flex items-center gap-2">
                        <span className="size-3 rounded-full bg-[#ff5f56]" />
                        <span className="size-3 rounded-full bg-[#ffbd2e]" />
                        <span className="size-3 rounded-full bg-[#27c93f]" />
                    </div>
                    <div className="flex flex-1 justify-center px-5">
                        <span className="rounded-lg bg-white px-3 py-1 text-[13px] text-gray-500">
                            demo.dotcms.com
                        </span>
                    </div>
                    <div className="w-[60px]" />
                </div>
            )}

            {/* Content area */}
            <div
                className="relative bg-white"
                style={
                    aspectRatio
                        ? { aspectRatio, overflow: 'hidden', ...(maxHeight ? { maxHeight } : {}) }
                        : undefined
                }>
                {/* Iframe — always rendering normally. Never hidden or filtered.
                    Applying opacity/filter to an iframe container breaks rendering in browsers. */}
                <div
                    ref={contentRef}
                    className="w-full [&_iframe]:block [&_iframe]:w-full [&_iframe]:border-none"
                    style={aspectRatio ? { height: '100%', minHeight: 0 } : { minHeight: '400px' }}
                />

                {/* Preview overlay — covers the iframe until the user reveals it.
                    The spring + blur animation is on the overlay lifting away,
                    not on the iframe, which avoids all browser filter/iframe issues. */}
                <div
                    className="absolute inset-0 z-10 flex items-center justify-center"
                    style={{
                        opacity: revealed ? 0 : 1,
                        filter: revealed ? 'blur(10px)' : 'blur(0px)',
                        transform: revealed ? 'scale(1.04)' : 'scale(1)',
                        pointerEvents: revealed ? 'none' : 'auto',
                        transition:
                            'opacity 800ms ease-in-out, filter 500ms ease-in-out, transform 250ms ease-in-out'
                    }}>
                    <Image
                        src={imageSrc}
                        alt="Demo preview"
                        fill
                        className="object-cover object-left-top"
                        priority
                    />

                    {/* Gradient scrim for button contrast */}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />

                    {/* CTA — fades in once iframe is ready */}
                    <div
                        className="relative z-10 transition-[opacity,transform] duration-500"
                        style={{
                            opacity: iframeReady ? 1 : 0,
                            transform: iframeReady ? 'translateY(0)' : 'translateY(6px)',
                            pointerEvents: iframeReady ? 'auto' : 'none'
                        }}>
                        <button
                            onClick={handleReveal}
                            className="inline-flex cursor-pointer items-center gap-2.5 whitespace-nowrap rounded-full bg-white px-7 py-3.5 text-[15px] font-semibold text-gray-900 shadow-[0_4px_24px_rgba(0,0,0,0.25),0_1px_4px_rgba(0,0,0,0.15)] transition-all duration-200 hover:scale-[1.04] hover:shadow-[0_8px_32px_rgba(0,0,0,0.3)] active:scale-[0.98]">
                            <span>{buttonLabel}</span>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="shrink-0 text-[hsl(293_69%_49%)]">
                                <polygon points="5 3 19 12 5 21 5 3" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
}

export default IFrameCode;
